<template>
    <div class="content-wrapper">
        <!-- Page Loader -->
        <div class="vld-parent">
            <loading :active.sync="isLoading" :is-full-page="fullPage"></loading>
        </div>

        <breadcrumb :page="$t('form.employee.absenceList')"></breadcrumb>

        <div class="content">
            <div class="container-fluid">

                <b-alert v-model="alert" :variant="alertClass" dismissible fade>{{alertMessage}}</b-alert>

                <!-- Filters -->
                <div class="card">
                    <div class="card-header bg-light">
                        <div class="card-title">{{$t('filters')}}</div>
                        <div class="text-right">
                            <b-button variant="primary" size="sm" @click="detailedSearch = !detailedSearch">
                                {{$t('button.detailedSearch')}}</b-button>
                            <b-button variant="default" size="sm" @click="clearSearch()">{{$t('button.resetFilter')}}
                            </b-button>
                        </div>
                    </div>

                    <div class="card-body">
                        <form role="form">

                            <div class="row">
                                <div class="col-sm-6">
                                    <div class="form-group">
                                        <label>{{$t('form.employee.searchCriteria')}}</label>
                                        <v-select @input="loadPreset" label="name" :options="presetList"
                                            v-model="preset">
                                        </v-select>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-sm-3">
                                    <div class="form-group">
                                        <label>{{$t('form.employee.absenceType')}}</label>
                                        <v-select label="FullName" @input="searchAbsences" :options="staffList"
                                            v-model="search.staff_id" :reduce="staffList => staffList.ID"></v-select>
                                    </div>
                                </div>

                                <div class="col-sm-3">
                                    <div class="form-group">
                                        <label>{{$t('form.employee.absenceBy')}}</label>
                                        <v-select label="name" @input="searchAbsences" :options="users"
                                            v-model="search.by" :reduce="users => users.id"></v-select>
                                    </div>
                                </div>

                                <div class="col-sm-3">
                                    <div class="form-group">
                                        <label>{{$t('form.employee.absenceStatus')}}</label>
                                        <v-select @input="searchAbsences" :options="statuses" v-model="search.status">
                                        </v-select>
                                    </div>
                                </div>

                                <div class="col-sm-3">
                                    <div class="form-group">
                                        <label>{{$t('form.employee.absenceReason')}}</label>
                                        <v-select label="name" @input="searchAbsences" :options="reasons"
                                            v-model="search.reason_id" :reduce="reasons => reasons.id">
                                        </v-select>
                                    </div>
                                </div>
                            </div>

                            <div class="row" v-if="detailedSearch">
                                <div class="col-12">
                                    <hr>
                                </div>
                                <div class="col-sm-6">
                                    <div class="form-group">
                                        <input type="text" v-model="preset.name" class="form-control"
                                            :placeholder="$t('form.employee.presetName')">
                                    </div>
                                </div>
                                <div class="col-sm-6">
                                    <button type="button" class="btn btn-primary"
                                        @click="savePreset">{{$t('button.save')}}</button>
                                    <button type="button" class="btn btn-danger" v-show="presetSelected"
                                        @click="deletePreset">{{$t('button.delete')}}</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- List -->
                <div class="card">
                    <div class="card-header bg-light">
                        <div class="card-title">{{$t('form.employee.absenceList')}}</div>
                    </div>
                    <div class="card-body table-responsive p-0">
                        <table class="table table-hover" v-if="absences.length">
                            <thead>
                                <tr class="header">
                                    <th>{{$t('form.employee.absenceType')}}</th>
                                    <th>{{$t('form.employee.absenceReason')}}</th>
                                    <th>{{$t('form.employee.absenceDuration')}}</th>
                                    <th class="text-center">{{$t('form.employee.absenceStatus')}}</th>
                                    <th>{{$t('form.employee.absenceBy')}}</th>
                                    <th>{{$t('form.employee.absenceRemarks')}}</th>
                                    <th>{{$t('form.employee.deductible')}}</th>
                                    <th class="text-right">{{$t('form.employee.action')}}</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="(absence, index) in absences" v-bind:key="index">
                                    <td>{{`${absence.staff.FirstName} ${absence.staff.LastName}`}}</td>
                                    <td>{{absence.reason_id ? absence.reason.name : ''}}</td>
                                    <td>
                                        {{$t('dashboard.durationValue', {
                                            from: $options.filters.formatDate(absence.from, datetimeFormat),
                                            to: $options.filters.formatDate(absence.to, datetimeFormat),
                                            days: absence.days
                                        })}}
                                    </td>
                                    <td class="text-center">
                                        <i class="fas fa-check-circle text-success"
                                            :title="$t('form.employee.approved')"
                                            v-if="absence.status === 'approved'"></i>
                                        <i class="fas fa-times-circle text-danger" :title="$t('form.employee.rejected')"
                                            v-else-if="absence.status === 'rejected'"></i>
                                        <i class="fas fa-minus-circle text-secondary"
                                            :title="$t('form.employee.pending')" v-else></i>
                                    </td>
                                    <td>{{absence.user ? absence.user.name : ''}}</td>
                                    <td>{{absence.remarks}}</td>
                                    <td>
                                        <div class="form-check">
                                            <input class="form-check-input" type="checkbox"
                                                v-model="absence.deductible">
                                        </div>
                                    </td>
                                    <td class="text-right">
                                        <button v-if="permissions.acceptEmployeeAbsences" class="btn btn-success btn-sm"
                                            :title="$t('form.employee.approve')"
                                            @click="accept(index, absence.deductible)">
                                            <i class="fas fa-check-circle"></i>
                                        </button>
                                        <button v-if="permissions.rejectEmployeeAbsences" class="btn btn-danger btn-sm"
                                            :title="$t('form.employee.reject')" @click="reject(index)">
                                            <i class="fas fa-times-circle"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p class="p-3 m-0" v-else>{{$t('noRecord')}}</p>
                    </div>

                    <pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="5"
                        @paginate="paginate()">
                    </pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: ['permissions', 'dateFormat', 'datetimeFormat'],

        data() {
            return {
                alert: false,
                alertClass: '',
                alertMessage: '',

                isLoading: false,
                fullPage: true,

                detailedSearch: false,
                presetSelected: false,
                request_source: '',

                pagination: {
                    current_page: 1
                },

                search: {},

                preset: {
                    name: '',
                    entity: 'absence',
                    value: {}
                },

                statuses: [],
                absences: [],
                users: [],
                staffList: [],
                presetList: [],
                reasons: []
            }
        },

        async mounted() {
            this.isLoading = true;

            if (!this.permissions.viewEmployeeAbsences) {
                return this.$router.push('/');
            }

            await this.getInitialData();

            if (localStorage.getItem('filter-absence')) {
                this.search = JSON.parse(localStorage.getItem('filter-absence'));
            }

            this.getAbsences();
            this.getPresets();
        },

        watch: {
            search: {
                handler() {
                    localStorage.setItem('filter-absence', JSON.stringify(this.search));
                },
                deep: true
            }
        },

        methods: {

            async getInitialData() {
                await axios
                    .get("/api/absences/initial-data")
                    .then(response => {
                        this.statuses = response.data.statuses;
                        this.users = response.data.users;
                        this.reasons = response.data.reasons;

                        this.staffList = response.data.staff.map(s => {
                            s.FullName = `${s.FirstName} ${s.LastName}`;
                            return s;
                        });
                    })
                    .catch(error => console.log('error fetching initial data'));
            },

            // Clear search input fields
            clearSearch() {
                this.search = {};
                this.searchAbsences();
            },

            async updateStatus(index, status = '', deductible = false) {
                let remarks = window.prompt(this.$t('form.employee.addRemarks'));

                if (remarks === null) return;

                this.isLoading = true;
                this.clearAlert();

                try
                {
                    let response = await axios.put(`/api/absences/${this.absences[index].id}`, {
                            status,
                            deductible,
                            remarks
                        });



                        if (response.data)
                        {
                            this.showAlert(response.data.message);
                            this.absences[index] = response.data.data;
                            this.isLoading = false;
                        }


                }
                catch (error)
                {
                    if (error.response)
                    {
                        this.showAlert(error.response.data.message, error.response.data.type);
                        this.isLoading = false;
                    }
                }

            },

            accept(index, deductible) {
                if (!this.permissions.acceptEmployeeAbsences) return;

                this.updateStatus(index, 'approved', deductible);
            },

            reject(index) {
                if (!this.permissions.rejectEmployeeAbsences) return;

                this.updateStatus(index, 'rejected');
            },

            getAbsences() {
                let source = axios.CancelToken.source();

                if (this.request_source != '')
                    this.request_source.cancel('Operation canceled by the user.');

                this.request_source = source;

                this.isLoading = true;

                axios
                    .get('/api/absences?page=' + this.pagination.current_page, {
                        params: this.search,
                        cancelToken: this.request_source.token
                    })
                    .then(response => {
                        this.absences = response.data.data.data;

                        this.pagination = response.data.pagination;

                        this.isLoading = false;
                    })
                    .catch(error => {
                        this.isLoading = false;
                        console.log('error');
                    });
            },


            paginate() {
                this.getAbsences();
            },

            searchAbsences() {
                this.pagination.current_page = 1;
                this.getAbsences();
            },

            // Save a Preset with Unique Name
            savePreset() {
                this.isLoading = true;
                this.alert = false;

                this.preset.value = JSON.stringify(this.search);

                axios
                    .post('/api/presets', this.preset)
                    .then(response => {
                        this.isLoading = false;
                        this.showAlert(response.data.message);
                        this.getPresets();
                    }, error => {
                        this.isLoading = false;

                        if (error.response.status == 422) {
                            this.showAlert(error.response.data.errors.name[0], 'danger');
                        }
                    });
            },

            // Get Presets in Search Criteria
            getPresets() {
                axios
                    .get('/api/presets/?entity=' + this.preset.entity)
                    .then(response => {
                        this.presetList = response.data;
                    }, error => {
                        console.log(error.response);
                    });
            },

            // Get Search results according to selected Preset Name
            loadPreset() {
                if (!this.preset) {
                    this.preset = {
                        entity: 'staff'
                    };
                    this.clearSearch();
                    this.alert = false;
                    this.presetSelected = false;
                    return;
                }
                this.alert = false;
                this.presetSelected = true;
                this.search = JSON.parse(this.preset.value);
                this.searchAbsences();
            },

            // Delete a selected Preset
            deletePreset() {
                let conf = confirm(this.$t('form.validation.delete'));

                if (conf === true) {
                    axios
                        .delete("/api/presets/" + this.preset.id)
                        .then(response => {
                            if (response.data.status == 'success') {
                                this.showAlert(response.data.message);
                                this.getPresets();
                                this.clearSearch();
                                this.preset.name = '';
                                this.preset.value = {};
                                this.presetSelected = false;
                            }
                        })
                        .catch(error => {
                            this.isLoading = false;
                        });
                }
            },

            showAlert(msg, cls = 'success') {
                this.alertMessage = msg;
                this.alertClass = cls;
                this.alert = true;
            },

            clearAlert() {
                this.alertMessage = '';
                this.alertClass = '';
                this.alert = false;
            }
        }
    }

</script>
